// src/pages/Send.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;

export default function Send() {
  const socket = useMemo(() => io(SOCKET_URL, {
    path: '/socket.io',          // 與 Nginx/Server 對齊
    reconnection: true,
    withCredentials: true
  }), []);

  const [msg, setMsg] = useState('');
  const [toast, setToast] = useState(false);
  const [connected, setConnected] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const maxLen = 200;
  const presets = ['我在餐桌區找我討論！', '我在鋼琴區找我討論！', '我在廚房區找我討論！', '我在廁所區找我討論！', '我在臥室區找我討論！'];

  // 連線狀態日誌（方便你排錯）
  useEffect(() => {
    const onConnect = () => { setConnected(true); setErrorMsg(''); console.log('[send] socket connected', socket.id); };
    const onDisconnect = (r) => { setConnected(false); console.log('[send] socket disconnected', r); };
    const onError = (e) => { setConnected(false); setErrorMsg(e?.message || '連線失敗'); console.error('[send] connect_error', e); };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onError);
    socket.on('error', onError);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onError);
      socket.off('error', onError);
      socket.close();
    };
  }, [socket]);

  const send = async (e) => {
    e.preventDefault();
    const s = (msg || '').trim();
    if (!s) return;

    const clean = s.slice(0, maxLen);

    let viaSocket = false;
    try {
      if (socket.connected) {
        socket.emit('msg', clean);
        viaSocket = true;
      }
    } catch (err) {
      console.error('[send] socket emit failed:', err);
    }

    // 後援：若 socket 沒連上，就用 REST，確保入庫 + 後端廣播
    if (!viaSocket) {
      try {
        const r = await fetch('/api/wall/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: clean })
        });
        if (!r.ok) throw new Error('REST send failed');
      } catch (err) {
        console.error('[send] REST failed:', err);
        setErrorMsg('送出失敗，請稍後重試');
        return;
      }
    }

    setMsg('');
    setToast(true);
    setTimeout(() => setToast(false), 1500);
  };

  return (
    <div style={styles.root}>
      <main style={styles.main}>
        <h1 style={styles.h1}>發送到現場大螢幕</h1>

        {/* 連線狀態 */}
        <div style={{display:'flex',alignItems:'center',gap:8,margin:'8px 0 16px'}}>
          <span style={{
            width:10,height:10,borderRadius:'50%',
            background: connected ? '#10b981' : '#ef4444', boxShadow:'0 0 8px rgba(0,0,0,.3)'
          }} />
          <span style={{fontSize:12, color:'#cbd5e1'}}>
            {connected ? '已連線（即時送出）' : '未連線（將使用備援通道）'}
          </span>
        </div>

        <form onSubmit={send} style={styles.form}>
          <label style={styles.label}>你的留言</label>
          <textarea
            value={msg}
            onChange={(e)=>setMsg(e.target.value)}
            rows={4}
            placeholder={`想在大螢幕上說些什麼呢？（最多 ${maxLen} 字）`}
            maxLength={maxLen}
            style={styles.textarea}
          />
          <div style={styles.count}>{msg.length}/{maxLen}</div>

          <div style={styles.presetRow}>
            {presets.map(t => (
              <button
                key={t}
                type="button"
                onClick={()=>setMsg(t)}
                style={styles.presetBtn}
              >{t}</button>
            ))}
          </div>

          <button type="submit" style={styles.submit}>
            送出到大螢幕
          </button>

          <p style={styles.tip}>已連線到現場伺服器後，送出即可立即顯示在投影牆上。</p>
          {errorMsg && <p style={{...styles.tip, color:'#fca5a5'}}>{errorMsg}</p>}
        </form>

        {toast && <div style={styles.toast}>已送出！</div>}
      </main>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100svh',
    background: 'linear-gradient(to bottom, #0f172a, #1f2937)',
    color: '#fff',
    padding: 20,
    fontFamily: 'system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial',
  },
  main: { maxWidth: 480, margin: '0 auto' },
  h1: { fontSize: 24, fontWeight: 800, marginBottom: 16 },
  form: { background: 'rgba(255,255,255,.06)', borderRadius: 16, padding: 16, backdropFilter: 'blur(6px)' },
  label: { display: 'block', fontSize: 12, color: '#cbd5e1', marginBottom: 6 },
  textarea: {
    width: '100%', borderRadius: 12, padding: '10px 12px',
    background: 'rgba(0,0,0,.25)', border: '1px solid rgba(255,255,255,.14)',
    outline: 'none', color: '#fff', fontSize: 16,
  },
  count: { textAlign: 'right', fontSize: 12, color: '#94a3b8', marginTop: 6 },
  presetRow: { display:'flex', gap:8, margin:'10px 0', flexWrap:'wrap' },
  presetBtn: {
    padding: '8px 10px', borderRadius: 10,
    background: 'rgba(255,255,255,.1)', border: 'none',
    color: '#fff', cursor:'pointer'
  },
  submit: {
    width: '100%', padding: '12px 14px', borderRadius: 14,
    background: '#6366f1', color:'#fff', border:'none',
    fontWeight: 700, cursor:'pointer', marginTop: 6
  },
  tip: { textAlign:'center', fontSize:12, color:'#94a3b8', marginTop:10 },
  toast: {
    position:'fixed', left:'50%', transform:'translateX(-50%)',
    bottom:24, background:'rgba(0,0,0,.8)', color:'#fff',
    padding:'8px 14px', borderRadius:12
  },
};
